import {
  HorizontalConnectionPos,
  Overlay,
  OverlayRef,
  PositionStrategy,
  VerticalConnectionPos,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

const INFO_WINDOW_WIDTH = 320;

@Component({
    selector: 'gl-modal-tooltip',
    templateUrl: './modal-tooltip.component.html',
    styleUrls: ['./modal-tooltip.component.scss'],
    standalone: false
})
export class ModalTooltipComponent implements OnInit, OnDestroy {
  @ViewChild('cdkPortal', { static: false }) public cdkPortal: TemplateRef<any>;
  @ViewChild('infoArrow', { static: false }) public infoArrowEl: ElementRef<HTMLElement>;
  @ViewChild('infoWindow', { static: false }) public infoWindowEl: ElementRef<HTMLElement>;
  @ViewChild('infoWrapper', { static: false }) public infoWrapperEl: ElementRef<HTMLElement>;
  @Input() public error: boolean;
  @Input() public paddingWindow: string;
  @Input() public widthWindow: string;
  @Input() private offset: number;
  @Input() private position: 'bottom' | 'right' | 'left' | 'top';
  private tooltipTimeout: number;
  private portal: TemplatePortal;
  private overlayRef: OverlayRef;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
  ) {}

  public ngOnInit(): void {
    if (!this.offset) {
      this.offset = 12;
    }
    if (!this.position) {
      this.position = 'top';
    }
  }

  public ngOnDestroy(): void {
    if (this.tooltipTimeout) {
      window.clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = undefined;
    } else {
      this.destroyTooltipComponent();
    }
  }

  @HostListener('mouseenter') public onMouseEnter() {
    this.tooltipTimeout = window.setTimeout(() => {
      this.generateTooltipComponent();
      this.tooltipTimeout = undefined;
    }, 200);
  }

  @HostListener('mouseleave') public onMouseLeave() {
    if (this.tooltipTimeout) {
      window.clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = undefined;
    } else {
      this.destroyTooltipComponent();
    }
  }

  private generateTooltipComponent() {
    const isSpaceForRight =
      window.innerWidth - this.elRef.nativeElement.getBoundingClientRect().left > INFO_WINDOW_WIDTH;
    let fakePosition = this.position;
    if (this.position === 'right' && !isSpaceForRight) {
      fakePosition = 'left';
    }

    this.overlayRef = this.overlay.create({
      positionStrategy: this.generatePositionStrategy(fakePosition),
    });
    this.portal = new TemplatePortal(this.cdkPortal, this.viewContainerRef);
    this.overlayRef.attach(this.portal);
    window.setTimeout(() => {
      if (this.paddingWindow) {
        this.infoWrapperEl.nativeElement.style.padding = `${this.paddingWindow}px`;
      }

      if (this.widthWindow) {
        this.infoWrapperEl.nativeElement.style.maxWidth = `${this.widthWindow}px`;
      }
      this.calculateArrowPosition(fakePosition);
      this.renderer.setStyle(this.infoWindowEl.nativeElement, 'visibility', 'visible');
    });
  }

  private calculateArrowPosition(position: 'bottom' | 'right' | 'left' | 'top') {
    const infoTriggerRect = this.elRef.nativeElement.getBoundingClientRect();
    const infoWindowRect = this.infoWindowEl.nativeElement.getBoundingClientRect();
    const ARROW_BORDER = this.infoArrowEl.nativeElement.getBoundingClientRect().width / 2;

    const centerFromTop =
      infoTriggerRect.top + infoTriggerRect.height / 2 - infoWindowRect.top - ARROW_BORDER + 'px';
    const centerFromLeft =
      infoTriggerRect.left + infoTriggerRect.width / 2 - infoWindowRect.left - ARROW_BORDER + 'px';

    if (position === 'top') {
      this.setStyleArrow('top', infoWindowRect.height - ARROW_BORDER + 'px');
      this.setStyleArrow('left', centerFromLeft);
      this.setStyleArrow('transform', 'rotate(-135deg)');
    } else if (position === 'bottom') {
      this.setStyleArrow('bottom', infoWindowRect.height - ARROW_BORDER + 'px');
      this.setStyleArrow('left', centerFromLeft);
      this.setStyleArrow('transform', 'rotate(45deg)');
    } else if (position === 'right') {
      this.setStyleArrow('top', centerFromTop);
      this.setStyleArrow('left', -ARROW_BORDER + 'px');
      this.setStyleArrow('transform', 'rotate(-45deg)');
    } else if (position === 'left') {
      this.setStyleArrow('top', centerFromTop);
      this.setStyleArrow('left', infoWindowRect.width - ARROW_BORDER + 'px');
      this.setStyleArrow('transform', 'rotate(135deg)');
    }
  }

  private setStyleArrow(styleType: string, value: string) {
    this.renderer.setStyle(this.infoArrowEl.nativeElement, styleType, value);
  }

  private destroyTooltipComponent() {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  private generatePosition(
    originX: HorizontalConnectionPos,
    originY: VerticalConnectionPos,
    overlayX: HorizontalConnectionPos,
    overlayY: VerticalConnectionPos,
  ) {
    return this.overlay
      .position()
      .flexibleConnectedTo(this.elRef)
      .withPositions([
        {
          originX,
          originY,
          overlayX,
          overlayY,
        },
      ])
      .withViewportMargin(10);
  }

  private generatePositionStrategy(
    position: 'bottom' | 'right' | 'left' | 'top',
  ): PositionStrategy {
    let overlayConfig;
    if (position === 'bottom') {
      overlayConfig = this.generatePosition('center', 'top', 'center', 'top').withDefaultOffsetY(
        this.elRef.nativeElement.clientHeight + this.offset,
      );
    } else if (position === 'top') {
      overlayConfig = this.generatePosition(
        'center',
        'bottom',
        'center',
        'bottom',
      ).withDefaultOffsetY(-this.elRef.nativeElement.clientHeight - this.offset);
    } else if (position === 'left') {
      overlayConfig = this.generatePosition('end', 'center', 'end', 'center').withDefaultOffsetX(
        -this.elRef.nativeElement.clientWidth - this.offset,
      );
    } else if (position === 'right') {
      overlayConfig = this.generatePosition(
        'start',
        'center',
        'start',
        'center',
      ).withDefaultOffsetX(this.elRef.nativeElement.clientWidth + this.offset);
    }
    return overlayConfig as PositionStrategy;
  }
}
